/**
 * ShipmentDetailsModal - Detalhes da remessa (Logística, Fase 2).
 *
 * Abas: Resumo, Itens, Vínculos e Linha do tempo. Ações conforme a situação atual
 * (mesmo mapa do servidor) e as permissões do menu `log_shipments`.
 *
 * RELEASE: 2026-10-16 v2.3.0 - Cabeçalho: avanço principal visível (entregue em verde) e demais ações no menu "...".
 * RELEASE: 2026-10-16 v2.2.0 - "Voltar para preparação" (secundário) quando a remessa está pronta.
 * RELEASE: 2026-10-16 v2.1.0 - Tipo do destinatário + olho para a ficha oficial; olho nos Relacionamentos;
 *          aba Vínculos com subabas (O.S. reutiliza ServiceOrdersEmbeddedList); aba Resumo antiga
 *          removida e Andamentos renomeada para Resumo com a seção Observação (padrão Descrição de Tarefas).
 * RELEASE: 2026-10-16 v2.0.0 - Leiaute do modal de O.S. (cabeçalho compacto, trilha de situação,
 *          abas, barra lateral 360px) e aba Andamentos com comentários/anexos (timeline compartilhada).
 * RELEASE: 2026-10-16 v1.1.0 - Fase 3: aba Itens editável antes do envio e, na entrega a
 *          cliente com equipamentos, escolha obrigatória do endereço Casa do cliente.
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { AlertTriangle, BarChart3, Tag, CalendarDays, CheckCircle2, Clock, Copy, DollarSign, FileText, Hash, History, MoreHorizontal, Link2, Loader2, MapPin, MessageSquare, Package, Pencil, RotateCcw, Send, Timer, Trash2, Truck, User, X } from 'lucide-react';
import { ShipmentInsightsTab } from './ShipmentInsightsTab';
import { formatShipmentDuration, getShipmentDeadline } from '@/lib/shipmentDeadline';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { TooltipProvider } from '@/components/ui/tooltip';
import { PhaseTrail } from '@/components/shared/PhaseTrail';
import { DetailsContentCard, DetailsInfoRow, DetailsRelationshipRow, DetailsSidebarCard, DetailsSidebarShell } from '@/components/shared/details-modal/DetailsModalPrimitives';
import { ServiceOrderActivityTab, type ActivityTimelineAdapter } from '@/components/service-orders/details/ServiceOrderActivityTab';
import { useShipmentTimeline, uploadShipmentAttachment, getShipmentAttachmentUrl } from '@/hooks/useShipmentTimeline';
import type { ServiceOrderTimelineItem } from '@/hooks/useServiceOrderDetails';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { describeShipmentEvent, getShipmentEventNote, getShipmentEventPresentation } from './shipmentEventPresentation';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useAuth } from '@/hooks/useAuth';
import { useServiceOrderModuleEnabled } from '@/hooks/useServiceOrderModule';
import { canViewShipmentLink, recipientKindLabel } from '@/lib/shipmentLinkPermissions';
import { useShipmentDeliveryLocations, useShipmentDetails, useShipmentMutations } from '@/hooks/useShipments';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShipmentItemsPanel } from './ShipmentItemsPanel';
import { ShipmentDeadlineChip } from './ShipmentDeadlineChip';
import { ShipmentTrackingCode } from './ShipmentTrackingCode';
import { ShipmentRecordOpener, type ShipmentRecordRef } from './ShipmentRecordOpener';
import { TaskDescriptionSection } from '@/components/tasks/modal/components/TaskDescriptionSection';
import { ServiceOrdersEmbeddedList } from '@/components/service-orders/ServiceOrdersEmbeddedList';
import { ShipmentStatusBadge } from './ShipmentStatusBadge';
import { ShipmentFormDialog, formatAddress } from './ShipmentFormDialog';
import { SHIPMENT_ACTION_LABEL, shipmentActionLabel, splitShipmentActions, SHIPMENT_STATUS_META, SHIPMENT_STATUS_COLOR, SHIPMENT_TRANSITIONS, isShipmentFinal, type ShipmentStatus } from './shipmentStatus';

const ENTITY_LABEL: Record<string, string> = { service_order: 'Ordem de Serviço', deal: 'Negócio', task: 'Tarefa', customer: 'Cliente', vehicle: 'Veículo' };
/** Subabas de Vínculos (ordem fixa) */
const LINK_TABS: { type: string; label: string }[] = [
  { type: 'service_order', label: 'O.S.' }, { type: 'deal', label: 'Negócios' }, { type: 'task', label: 'Tarefas' },
  { type: 'customer', label: 'Clientes' }, { type: 'vehicle', label: 'Veículos' },
];
/** Caminho principal exibido na trilha de situação. */
const TRAIL_PATH: ShipmentStatus[] = ['draft', 'preparing', 'ready', 'shipped', 'in_transit', 'delivered'];

/** Adaptador da timeline compartilhada (mesma aba Andamentos da O.S.). */
const SHIPMENT_TIMELINE_ADAPTER: ActivityTimelineAdapter = {
  uploadAttachment: uploadShipmentAttachment,
  getAttachmentUrl: getShipmentAttachmentUrl,
  presentEvent: getShipmentEventPresentation,
  describeEvent: describeShipmentEvent,
  getEventNote: getShipmentEventNote,
  refreshQueryKey: ['shipment-timeline'],
};

/** Histórico estrutural (somente eventos) no leiaute de ServiceOrderHistoryTab. */
function ShipmentHistoryList({ events, loading }: { events: ServiceOrderTimelineItem[]; loading: boolean }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (!events.length) return <Empty text="Sem histórico ainda." />;
  return (
    <div className="relative py-2 pl-3 pr-1"><div className="absolute bottom-3 left-[26px] top-3 w-px bg-border/60" />
      <ol className="space-y-3">{events.map((e) => {
        const { Icon, tone } = getShipmentEventPresentation(e.event_type);
        return (
          <li key={e.id} className="relative flex gap-3">
            <div className={cn('relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-background', tone)}><Icon className="h-4 w-4" /></div>
            <div className="min-w-0 flex-1 pb-2 text-sm">
              <span className="font-medium">{e.author_name || 'Sistema'}</span>{' '}
              <span className="text-muted-foreground">{describeShipmentEvent(e).toLowerCase()}</span>
              <span className="text-xs text-muted-foreground/70"> · {fmtDateTime(e.created_at)}</span>
            </div>
          </li>
        );
      })}</ol>
    </div>
  );
}

const fmtDateTime = (v: string | null) => (v ? format(new Date(v), "dd/MM/yyyy HH:mm", { locale: ptBR }) : '—');
const fmtDate = (v: string | null) => (v ? format(new Date(`${v}T12:00:00`), 'dd/MM/yyyy') : '—');

interface Props {
  shipmentId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Abre outra remessa (ex.: retorno recém-criado). */
  onOpenShipment?: (id: string) => void;
}

export function ShipmentDetailsModal({ shipmentId, open, onOpenChange, onOpenShipment }: Props) {
  const { canEdit, canCreate, canDelete, canView, isAdmin } = usePermissionsContext();
  const { currentTenant } = useAuth();
  const serviceOrdersEnabled = useServiceOrderModuleEnabled();
  // Permissão por módulo do vínculo: sem acesso, a subaba e o olho somem
  const canSeeLink = (type: string) => canViewShipmentLink(type, { isAdmin, canView, serviceOrdersEnabled, tasksEnabled: !!(currentTenant as any)?.tasks_enabled });
  const { data: s, isLoading } = useShipmentDetails(open ? shipmentId : null);
  const { changeStatus, createReturn, remove, upsert } = useShipmentMutations();
  const [record, setRecord] = useState<ShipmentRecordRef | null>(null);
  const [linkTab, setLinkTab] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<ShipmentStatus | null>(null);
  const [deliveredAt, setDeliveredAt] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deliveryLocation, setDeliveryLocation] = useState<string | null>(null);
  // Relógio de 1 minuto: atualiza a duração em trânsito e a faixa de prazo
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, [open]);
  const deadline = s ? getShipmentDeadline(s.expected_delivery_date, s.delivered_at, s.status, now) : null;

  const allowEdit = isAdmin || canEdit('log_shipments');
  const allowDelete = isAdmin || canDelete('log_shipments');
  const allowCreate = isAdmin || canCreate('log_shipments');

  // Fase 3: entrega de equipamentos a cliente exige endereço Casa do cliente (prestador recebe direto)
  const needsLocation = !!s && pending === 'delivered' && s.direction === 'outbound' && s.has_serial_items && !s.recipient_is_provider;
  const { data: locations = [] } = useShipmentDeliveryLocations(needsLocation);

  const next = s ? SHIPMENT_TRANSITIONS[s.status].filter((t) => (t === 'cancelled' ? allowDelete : allowEdit)) : [];

  /** Confirma a mudança (entregue pede a data). */
  const applyStatus = async () => {
    if (!s || !pending) return;
    await changeStatus.mutateAsync({ id: s.id, status: pending, deliveredAt: pending === 'delivered' ? new Date(deliveredAt).toISOString() : null, locationId: needsLocation ? deliveryLocation : null });
    setPending(null);
  };

  const timeline = useShipmentTimeline(open ? shipmentId : null);
  const [tab, setTab] = useState<'activity' | 'items' | 'links' | 'insights' | 'history'>('activity');
  const isMobile = useIsMobile();
  const final = s ? isShipmentFinal(s.status) : true;
  const historyEvents = (timeline.timeline.data ?? []).filter((i) => i.kind === 'event');
  const money = (v: number | null) => (v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : null);
  const openTransition = (t: ShipmentStatus) => { setDeliveredAt(format(new Date(), "yyyy-MM-dd'T'HH:mm")); setPending(t); };

  // Trilha: caminho principal + situação final atual quando fora dele
  const trail = s ? [...TRAIL_PATH, ...(TRAIL_PATH.includes(s.status) ? [] : [s.status])].map((st, i) => ({
    id: st, name: SHIPMENT_STATUS_META[st].label, color: SHIPMENT_STATUS_COLOR[st], sortOrder: i,
  })) : [];

  const tabs = s ? [
    { id: 'activity' as const, label: 'Resumo', icon: MessageSquare, count: undefined },
    { id: 'items' as const, label: 'Itens', icon: Package, count: s.items.length },
    { id: 'links' as const, label: 'Vínculos', icon: Link2, count: s.links.filter((l) => canSeeLink(l.entity_type)).length },
    // 2026-10-18: aba Indicadores (Gantt das situações)
    { id: 'insights' as const, label: 'Indicadores', icon: BarChart3, count: undefined },
    { id: 'history' as const, label: 'Histórico', icon: History, count: undefined },
  ] : [];

  const sidebar = s && (
    <DetailsSidebarShell mobile={isMobile}>
      <DetailsSidebarCard title="Destinatário" icon={User}>
        {/* Prazo da entrega (padrão "Informações básicas" de Tarefas): verde no prazo, âmbar hoje, vermelho atrasado */}
        <ShipmentDeadlineChip deadline={deadline} className="mb-1" />
        <DetailsInfoRow icon={Timer} label="Duração">{formatShipmentDuration(s.shipped_at, s.delivered_at, now)}</DetailsInfoRow>
        <DetailsInfoRow icon={Tag} label="Tipo">{recipientKindLabel(s.recipient_customer_id, s.recipient_roles)}</DetailsInfoRow>
        <DetailsRelationshipRow icon={User} label="Nome" value={s.recipient_name}
          onView={() => setRecord(s.recipient_customer_id ? { type: 'customer', id: s.recipient_customer_id } : { type: 'person', id: s.recipient_person_id!, name: s.recipient_name })} />
        <DetailsInfoRow icon={MapPin} label="Endereço"><span className="line-clamp-3 whitespace-normal">{formatAddress(s.address)}</span></DetailsInfoRow>
      </DetailsSidebarCard>
      <DetailsSidebarCard title="Transporte" icon={Truck}>
        <DetailsInfoRow icon={Truck} label="Transportadora">{s.carrier_name}</DetailsInfoRow>
        <DetailsInfoRow icon={Package} label="Serviço">{s.carrier_service}</DetailsInfoRow>
        <DetailsInfoRow icon={Hash} label="Rastreio">
          <ShipmentTrackingCode code={s.tracking_code} />
        </DetailsInfoRow>
        <DetailsInfoRow icon={DollarSign} label="Frete">{money(s.freight_cost)}</DetailsInfoRow>
      </DetailsSidebarCard>
      <DetailsSidebarCard title="Datas" icon={CalendarDays}>
        <DetailsInfoRow icon={CalendarDays} label="Previsão">{s.expected_delivery_date ? fmtDate(s.expected_delivery_date) : null}</DetailsInfoRow>
        <DetailsInfoRow icon={Send} label="Enviado em">{s.shipped_at ? fmtDateTime(s.shipped_at) : null}</DetailsInfoRow>
        <DetailsInfoRow icon={CheckCircle2} label="Entregue em">{s.delivered_at ? fmtDateTime(s.delivered_at) : null}</DetailsInfoRow>
        <DetailsInfoRow icon={Clock} label="Criada em">{fmtDateTime(s.created_at)}</DetailsInfoRow>
      </DetailsSidebarCard>
      <DetailsSidebarCard title="Vínculos" icon={Link2}>
        {s.links.length === 0 ? <p className="py-1 text-xs italic text-muted-foreground">Nenhum registro vinculado</p>
          : s.links.map((l) => <DetailsRelationshipRow key={`${l.entity_type}-${l.entity_id}`} icon={Link2}
              label={ENTITY_LABEL[l.entity_type] ?? l.entity_type} value={l.label || `#${l.entity_id.slice(0, 8).toUpperCase()}`}
              onView={canSeeLink(l.entity_type) ? () => setRecord({ type: l.entity_type, id: l.entity_id }) : undefined} />)}
      </DetailsSidebarCard>
    </DetailsSidebarShell>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton
        className={cn('flex flex-col gap-0 overflow-hidden p-0', isMobile ? 'h-[100dvh] max-h-none w-screen max-w-none rounded-none border-0' : 'h-[97vh] w-[95vw] max-w-[95vw]')}>
        <VisuallyHidden.Root><DialogTitle>{s?.number ?? 'Remessa'}</DialogTitle><DialogDescription>Detalhes da remessa</DialogDescription></VisuallyHidden.Root>
        <TooltipProvider>
        {isLoading || !s ? (
          <div className="space-y-3 p-4"><Skeleton className="h-8 w-1/3" /><Skeleton className="h-6 w-full" /><Skeleton className="h-64 w-full" /></div>
        ) : (
          <>
            {/* Cabeçalho compacto (padrão do modal de O.S.) */}
            <header className="flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-2.5">
              <Truck className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-semibold">{s.number}</h2>
              <ShipmentStatusBadge status={s.status} />
              <span className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                {s.direction === 'return' ? `Retorno de ${s.return_of_number ?? ''}` : 'Envio'}
              </span>
              <span className="min-w-0 truncate text-xs text-muted-foreground">· {s.recipient_name}</span>
              <div className="ml-auto flex flex-wrap items-center gap-1.5">
                {/* v2.3.0: só o avanço principal fica visível; "Marcar entregue" em verde (etapa final) */}
                {splitShipmentActions(s.status, next).primary.map((t) => (
                  <Button key={t} size="xs" disabled={changeStatus.isPending} onClick={() => openTransition(t)}
                    className={t === 'delivered' ? 'bg-success text-success-foreground hover:bg-success/90' : undefined}>{shipmentActionLabel(s.status, t)}</Button>
                ))}
                {allowCreate && s.direction === 'outbound' && s.status === 'delivered' && (
                  <Button size="xs" variant="outline" disabled={createReturn.isPending}
                    onClick={async () => { const id = await createReturn.mutateAsync(s.id); onOpenShipment?.(id); }}>
                    <RotateCcw className="mr-1 h-3.5 w-3.5" />Gerar retorno
                  </Button>
                )}
                {allowEdit && !final && (
                  <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Editar remessa" onClick={() => setEditing(true)}><Pencil className="h-3.5 w-3.5" /></Button>
                )}
                {/* v2.3.0: retrocessos, desfechos e apagar rascunho no menu de ações */}
                {(() => {
                  const sec = splitShipmentActions(s.status, next).secondary;
                  const canDel = allowDelete && s.status === 'draft';
                  if (!sec.length && !canDel) return null;
                  return (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Mais ações"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {sec.map((t) => (
                          <DropdownMenuItem key={t} disabled={changeStatus.isPending} onClick={() => openTransition(t)}
                            className={['cancelled', 'lost', 'returned_to_sender'].includes(t) ? 'text-destructive focus:text-destructive' : undefined}>
                            {shipmentActionLabel(s.status, t)}
                          </DropdownMenuItem>
                        ))}
                        {canDel && sec.length > 0 && <DropdownMenuSeparator />}
                        {canDel && (
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setConfirmDelete(true)}>
                            <Trash2 className="mr-2 h-3.5 w-3.5" />Apagar rascunho
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                })()}
                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Fechar" onClick={() => onOpenChange(false)}><X className="h-4 w-4" /></Button>
              </div>
            </header>

            {/* Trilha de situação: clicar numa situação permitida abre a mesma confirmação dos botões */}
              <PhaseTrail phases={trail} currentPhaseId={s.status} readOnly={final || changeStatus.isPending}
                canMoveToPhase={(id) => next.includes(id as ShipmentStatus)}
                blockedReason={() => 'Transição não permitida a partir da situação atual'}
                onSelectPhase={(id) => openTransition(id as ShipmentStatus)} />

            {/* Faixa de prazo movida para o cartão Destinatário (menu lateral) */}

            {/* Abas no mesmo estilo de ServiceOrderModalTabs */}
            <div className="flex h-9 shrink-0 items-center overflow-x-auto border-b bg-muted/20 px-4">
              {tabs.map(({ id, label, icon: Icon, count }) => (
                <button key={id} type="button" onClick={() => setTab(id)}
                  className={cn('relative flex h-full shrink-0 items-center gap-1.5 px-3 text-xs font-medium transition-colors hover:text-foreground', tab === id ? 'text-primary' : 'text-muted-foreground')}>
                  <Icon className="h-3.5 w-3.5" /><span>{label}</span>
                  {!!count && <span className={cn('flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px]', tab === id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>{count}</span>}
                  {tab === id && <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-full bg-primary" />}
                </button>
              ))}
            </div>

            <div className={cn('flex min-h-0 flex-1', isMobile ? 'flex-col' : 'flex-row')}>
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {tab === 'activity' && (
                  <div className="space-y-4">
                  {isMobile && sidebar}
                  {/* Observação no padrão da seção Descrição do modal de Tarefas (texto simples no servidor) */}
                  <TaskDescriptionSection taskId={s.id} hideAttachments title="Observação" placeholder="Adicione uma observação sobre a remessa..."
                    plainContent={s.notes} readOnly={!allowEdit || final}
                    onSave={async (html) => {
                      const text = htmlToText(html).slice(0, 2000);
                      await upsert.mutateAsync({ id: s.id, data: {
                        carrier_person_id: s.carrier_person_id, carrier_service: s.carrier_service, tracking_code: s.tracking_code,
                        freight_cost: s.freight_cost, expected_delivery_date: s.expected_delivery_date,
                        responsible_user_id: s.responsible_user_id, notes: text,
                      } });
                    }} />
                  <ServiceOrderActivityTab orderId={s.id} comments={timeline.timeline.data ?? []} loading={timeline.timeline.isLoading}
                    locked={!allowEdit} addComment={timeline.addComment} updateComment={timeline.updateComment}
                    deleteComment={timeline.deleteComment} adapter={SHIPMENT_TIMELINE_ADAPTER} />
                  </div>
                )}
                {tab === 'items' && (
                  <DetailsContentCard title="Itens da remessa" icon={Package}>
                    <ShipmentItemsPanel shipment={s} editable={allowEdit && ['draft', 'preparing', 'ready'].includes(s.status)} />
                  </DetailsContentCard>
                )}
                {tab === 'links' && (s.links.length === 0 ? <Empty text="Nenhum registro vinculado." /> : (() => {
                  // Subabas com contador; só aparecem tipos que têm vínculo
                  const groups = LINK_TABS.map((t) => ({ ...t, links: s.links.filter((l) => l.entity_type === t.type) })).filter((g) => g.links.length && canSeeLink(g.type));
                  if (!groups.length) return <Empty text="Sem permissão para visualizar os registros vinculados." />;
                  const active = groups.find((g) => g.type === linkTab) ?? groups[0];
                  return (
                    <div className="flex min-h-0 flex-col rounded-xl border bg-card">
                      <div className="flex h-9 shrink-0 items-center overflow-x-auto border-b bg-muted/20 px-2">
                        {groups.map((g) => (
                          <button key={g.type} type="button" onClick={() => setLinkTab(g.type)}
                            className={cn('relative flex h-full shrink-0 items-center gap-1.5 px-3 text-xs font-medium', active.type === g.type ? 'text-primary' : 'text-muted-foreground hover:text-foreground')}>
                            {g.label}<span className={cn('flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px]', active.type === g.type ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>{g.links.length}</span>
                            {active.type === g.type && <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t-full bg-primary" />}
                          </button>
                        ))}
                      </div>
                      {active.type === 'service_order'
                        ? <ServiceOrdersEmbeddedList key={active.links.map((l) => l.entity_id).join(',')} ids={active.links.map((l) => l.entity_id)} />
                        : <div className="divide-y px-3">{active.links.map((l) => (
                            <DetailsRelationshipRow key={l.entity_id} icon={Link2} label={ENTITY_LABEL[l.entity_type] ?? l.entity_type}
                              value={l.label || `#${l.entity_id.slice(0, 8).toUpperCase()}`} onView={() => setRecord({ type: l.entity_type, id: l.entity_id })} />
                          ))}</div>}
                    </div>
                  );
                })())}
                {tab === 'insights' && <ShipmentInsightsTab createdAt={s.created_at} events={s.events}
                  expectedDeliveryDate={s.expected_delivery_date} deliveredAt={s.delivered_at} />}
                {tab === 'history' && <ShipmentHistoryList events={historyEvents} loading={timeline.timeline.isLoading} />}
              </div>
              {!isMobile && sidebar}
            </div>
          </>
        )}
        </TooltipProvider>

        {/* Confirmação da mudança de situação */}
        <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{pending && s ? shipmentActionLabel(s.status, pending) : ''}</AlertDialogTitle>
              <AlertDialogDescription>
                {pending === 'shipped' ? 'É preciso ter transportadora e código de rastreio. Os equipamentos passam para a transportadora no estoque.'
                  : pending === 'delivered' && s?.has_serial_items
                    ? (s.direction === 'return' ? 'Os equipamentos voltam ao endereço de estoque padrão.'
                      : s.recipient_is_provider ? 'Os equipamentos vão para o estoque do prestador.' : 'Escolha o endereço Casa do cliente que recebe os equipamentos.')
                    : 'Confirme a mudança de situação da remessa.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {pending === 'delivered' && (
              <div className="space-y-2">
                <Label>Data e hora da entrega</Label>
                <Input type="datetime-local" value={deliveredAt} onChange={(e) => setDeliveredAt(e.target.value)} />
                {needsLocation && (
                  <>
                    <Label>Endereço Casa do cliente</Label>
                    <Select value={deliveryLocation ?? ''} onValueChange={setDeliveryLocation}>
                      <SelectTrigger><SelectValue placeholder="Selecione o endereço" /></SelectTrigger>
                      <SelectContent>
                        {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {locations.length === 0 && <p className="text-xs text-destructive">Cadastre um endereço de estoque do tipo Casa do cliente.</p>}
                  </>
                )}
              </div>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel>Voltar</AlertDialogCancel>
              <AlertDialogAction disabled={changeStatus.isPending || (pending === 'delivered' && !deliveredAt) || (needsLocation && !deliveryLocation)}
                onClick={(e) => { e.preventDefault(); void applyStatus(); }}>
                {changeStatus.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirmar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Apagar rascunho?</AlertDialogTitle>
              <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Voltar</AlertDialogCancel>
              <AlertDialogAction onClick={async () => { if (s) { await remove.mutateAsync(s.id); onOpenChange(false); } }}>Apagar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {s && <ShipmentFormDialog open={editing} onOpenChange={setEditing} shipment={s} />}
        <ShipmentRecordOpener record={record} onClose={() => setRecord(null)} />
      </DialogContent>
    </Dialog>
  );
}

/** Converte o HTML do editor em texto simples (o servidor guarda observação sem HTML). */
function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html.replace(/<\/(p|div|li)>|<br\s*\/?>/gi, '\n'), 'text/html');
  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

function Empty({ text }: { text: string }) {
  return <p className="py-8 text-center text-sm text-muted-foreground">{text}</p>;
}
